import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { ImageLibraryItem, useImageLibrary } from '@/hooks/runware/image-library';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/errors/use-toast';
import { X } from 'lucide-react';
import { ImageMetadataSidebar } from './components/ImageMetadataSidebar';
import { ImageDisplay } from './components/ImageDisplay';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { useSmartNavigation } from '@/hooks/image-lightbox/useSmartNavigation';
import { useIsMobile } from '@/hooks/layout/use-mobile';

interface ImageDetailConfig {
  preloading?: {
    enabled: boolean;
    strategy: 'aggressive' | 'conservative' | 'smart';
    preloadDistance: number;
  };
  navigation?: {
    wheel: boolean;
    keyboard: boolean;
    clickZones: boolean;
  };
  mobile?: {
    fullscreen: boolean;
    sidebarPosition: 'bottom' | 'overlay' | 'hidden';
  };
}

interface ImageDetailProps<T = ImageLibraryItem> {
  image: T | null;
  isOpen: boolean;
  onClose: () => void;
  allImages?: T[];
  onNavigate?: (image: T) => void;
  loadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  config?: ImageDetailConfig;
  
  // Property accessors for generic types
  getImageUrl?: (item: T) => string;
  getImageId?: (item: T) => string;
  getImageDescription?: (item: T) => string | undefined;
  getUserId?: (item: T) => string | undefined;
  getFavoriteCount?: (item: T) => number | undefined;
  getIsFavorited?: (item: T) => boolean | undefined; // NEW: Get individual user favorite status
  
  // Custom sidebar rendering
  renderSidebar?: (item: T, context: SidebarContext<T>) => React.ReactNode;
  
  // Custom delete/download handlers
  onDelete?: (item: T) => Promise<void>;
  onDownload?: (item: T) => Promise<void>;
  checkOwnership?: (item: T) => Promise<boolean> | boolean;
  
  // Display options
  showFavoriteButton?: boolean;
  showDescription?: boolean;
  dropdownLabel?: string;
}

interface SidebarContext<T> {
  item: T;
  isOwner: boolean;
  isDeleteLoading: boolean;
  hasPrevious: boolean;
  hasNext: boolean;
  isLoadingMore: boolean;
  hasNextPage: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onLoadMore?: () => void;
  onDownload: () => void;
  onDelete: () => void;
}

const DEFAULT_CONFIG: ImageDetailConfig = {
  preloading: {
    enabled: true,
    strategy: 'smart',
    preloadDistance: 2
  },
  navigation: {
    wheel: true,
    keyboard: true,
    clickZones: true
  },
  mobile: {
    fullscreen: true,
    sidebarPosition: 'bottom'
  }
};

const ImageDetailComponent = <T = ImageLibraryItem>(props: ImageDetailProps<T>) => {
  const {
    image,
    isOpen,
    onClose,
    allImages = [],
    onNavigate,
    loadMore,
    hasNextPage = false,
    isFetchingNextPage = false,
    config = DEFAULT_CONFIG,
    
    // Property accessors with defaults for ImageLibraryItem
    getImageUrl = (item: T) => (item as any).image_url,
    getImageId = (item: T) => (item as any).id,
    getImageDescription = (item: T) => (item as any).description,
    getUserId = (item: T) => (item as any).user_id,
    getFavoriteCount = (item: T) => (item as any).favorite_count,
    getIsFavorited = (item: T) => (item as any).is_favorite, // NEW: Default accessor for is_favorite
    
    // Custom handlers
    renderSidebar,
    onDelete: customOnDelete,
    onDownload: customOnDownload,
    checkOwnership: customCheckOwnership,
    
    // Display options
    showFavoriteButton = true,
    showDescription = true,
    dropdownLabel
  } = props;

  const { isMobile } = useIsMobile();

  // Merge config with defaults and disable wheel navigation on mobile
  const mergedConfig = {
    ...DEFAULT_CONFIG,
    ...config,
    preloading: { ...DEFAULT_CONFIG.preloading, ...config?.preloading },
    navigation: { 
      ...DEFAULT_CONFIG.navigation, 
      ...config?.navigation,
      // Disable wheel navigation on mobile to allow normal scrolling
      wheel: isMobile ? false : (config?.navigation?.wheel ?? DEFAULT_CONFIG.navigation.wheel)
    },
    mobile: { ...DEFAULT_CONFIG.mobile, ...config?.mobile }
  };

  // Always call useSmartNavigation to maintain hook order
  const {
    hasPrevious,
    hasNext,
    canNavigateNext,
    handlePrevious,
    handleNext,
    handleWheel
  } = useSmartNavigation({
    currentItem: image,
    allItems: allImages,
    hasNextPage,
    isFetchingNextPage,
    loadMore,
    onNavigate,
    getItemId: getImageId,
    config: mergedConfig
  });

  // Delete functionality with improved error handling
  const { deleteImage } = useImageLibrary();
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  
  // Ownership check with error handling
  const [isOwner, setIsOwner] = useState(false);
  
  useEffect(() => {
    const checkOwnership = async () => {
      if (!image) return;
      
      try {
        if (customCheckOwnership) {
          const isOwner = await customCheckOwnership(image);
          setIsOwner(isOwner);
          return;
        }
        
        // Default ownership check for ImageLibraryItem
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        const userId = getUserId(image);
        if (session?.user && userId === session.user.id) {
          setIsOwner(true);
        } else {
          setIsOwner(false);
        }
      } catch (error) {
        console.error('Error checking ownership:', error);
        setIsOwner(false);
        // Don't show toast for ownership check failures - silent fallback
      }
    };
    
    checkOwnership();
  }, [image, customCheckOwnership, getUserId]);

  // Download with comprehensive error handling
  const handleDownload = async () => {
    if (!image) return;
    
    try {
      if (customOnDownload) {
        await customOnDownload(image);
        return;
      }
      
      // Default download logic
      const imageUrl = getImageUrl(image);
      const imageId = getImageId(image);
      
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `image-${imageId}.${imageUrl.split('.').pop() || 'webp'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Download started",
        description: "Your image is being downloaded.",
        variant: "success"
      });
    } catch (error) {
      console.error('Error downloading image:', error);
      toast({
        title: "Download failed",
        description: error instanceof Error ? error.message : "Failed to download image. Please try again.",
        variant: "error"
      });
    }
  };
  
  // Delete with improved error handling
  const handleDelete = () => {
    if (!image) return;
    
    const imageId = getImageId(image);
    if (!isOwner || !imageId) return;
    setShowDeleteConfirmation(true);
  };
  
  const confirmDelete = async () => {
    if (!image) return;
    
    const imageId = getImageId(image);
    if (!isOwner || !imageId) return;
    
    console.log('[ImageDetail] confirmDelete called with:', {
      imageId: imageId,
      isOwner: isOwner,
      hasCustomOnDelete: !!customOnDelete,
      timestamp: new Date().toISOString()
    });
    
    try {
      setIsDeleteLoading(true);
      
      if (customOnDelete) {
        console.log('[ImageDetail] Using custom delete handler');
        await customOnDelete(image);
      } else {
        console.log('[ImageDetail] Using default delete handler with deleteImage.mutateAsync');
        // Default delete logic for ImageLibraryItem
        await deleteImage.mutateAsync(imageId);
      }
      
      setShowDeleteConfirmation(false);
      onClose();
      toast({
        title: "Image deleted",
        description: "The image has been removed from your library.",
        variant: "success"
      });
    } catch (error) {
      console.error('[ImageDetail] Error deleting image:', error);
      toast({
        title: "Delete failed",
        description: error instanceof Error ? error.message : "There was a problem deleting the image. Please try again.",
        variant: "error"
      });
    } finally {
      setIsDeleteLoading(false);
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  // Now handle the conditional rendering AFTER all hooks have been called
  if (!image) {
    return (
      <Dialog open={false} onOpenChange={() => {}}>
        <DialogContent className="hidden">
          <DialogTitle className="sr-only">No Image</DialogTitle>
          <DialogDescription className="sr-only">No image selected</DialogDescription>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      {/* Enhanced confirmation dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirmation}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        title="Delete Image"
        description="Are you sure you want to delete this image? This action cannot be undone."
        confirmText={isDeleteLoading ? "Deleting..." : "Delete Image"}
        cancelText="Keep Image"
        confirmVariant="destructive"
        cancelVariant="default"
      />
      
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent 
          showCloseButton={false}
          className={`
            ${mergedConfig.mobile?.fullscreen 
              ? 'h-screen w-screen md:max-w-[95vw] md:max-h-[95vh] md:w-auto md:h-auto' 
              : 'max-w-[95vw] max-h-[95vh] w-auto h-auto'
            } 
            p-0 cursor-default
            ${isMobile ? 'overflow-auto !top-0 !left-0 !transform-none !translate-x-0 !translate-y-0' : ''}
          `}
          onPointerDownOutside={(e) => {
            // Prevent closing when clicking on navigation buttons
            const target = e.target as HTMLElement;
            if (target.closest('[data-nav-button]')) {
              e.preventDefault();
            }
          }}
        >
          <DialogTitle className="sr-only">Image Details</DialogTitle>
          <DialogDescription className="sr-only">
            {getImageDescription(image) || 'Generated image details and metadata'}
          </DialogDescription>
          
          <div className={`
            relative flex 
            ${mergedConfig.mobile?.sidebarPosition === 'bottom' ? 'flex-col md:flex-row' : 'flex-col md:flex-row'} 
            w-full h-full
          `}>
            
            {/* Close button */}
            <div className="fixed top-4 right-4 z-50">
              <DialogClose className="rounded-full h-8 w-8 flex items-center justify-center bg-background/90 hover:bg-background border border-border shadow-sm transition-all cursor-pointer">
                <X className="h-4 w-4" />
              </DialogClose>
            </div>
            
            {/* Main content area with sophisticated navigation */}
            <ImageDisplay 
              imageUrl={getImageUrl(image)} 
              description={getImageDescription(image)} 
              imageId={getImageId(image)}
              initialFavoriteCount={getFavoriteCount(image) || 0}
              initialIsFavorited={getIsFavorited(image)} // NEW: Pass favorite status from pagination data
              hasPrevious={hasPrevious}
              hasNext={canNavigateNext}
              onPrevious={mergedConfig.navigation?.clickZones ? handlePrevious : undefined}
              onNext={mergedConfig.navigation?.clickZones ? handleNext : undefined}
              onWheel={mergedConfig.navigation?.wheel ? handleWheel : undefined}
              showFavoriteButton={showFavoriteButton}
              showDescription={showDescription}
              dropdownLabel={dropdownLabel}
              key={getImageId(image)} // Force re-render when image changes
            />
            
            {/* Render custom sidebar or default metadata sidebar */}
            <div key={`sidebar-${getImageId(image)}-${image ? JSON.stringify(image).slice(0, 50) : 'none'}`}>
              {renderSidebar ? (
                <div key="custom-sidebar">
                  {renderSidebar(image, {
                    item: image,
                    isOwner,
                    isDeleteLoading,
                    hasPrevious,
                    hasNext,
                    isLoadingMore: isFetchingNextPage,
                    hasNextPage,
                    onPrevious: handlePrevious,
                    onNext: handleNext,
                    onLoadMore: loadMore,
                    onDownload: handleDownload,
                    onDelete: handleDelete
                  })}
                </div>
              ) : (
                <div key="default-sidebar">
                  <ImageMetadataSidebar 
                    image={image as ImageLibraryItem}
                    isOwner={isOwner}
                    isDeleteLoading={isDeleteLoading}
                    onDownload={handleDownload}
                    onDelete={handleDelete}
                    hasPrevious={hasPrevious}
                    hasNext={hasNext}
                    isLoadingMore={isFetchingNextPage}
                    hasNextPage={hasNextPage}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    onLoadMore={loadMore}
                  />
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Create a memoized version with proper typing
const ImageDetail = React.memo(ImageDetailComponent) as typeof ImageDetailComponent;

export default ImageDetail;
